import { ID, TaskCalendarOverride } from '@griply/types';

import { detachWrite } from '../detach-write';
import { FirestoreInstance } from '../types/firestore';
import { FirestoreCollections } from '../types/firestore-collections';

export type SetTaskCalendarOverrideOptions<
  TDocRef = unknown,
  TFieldValue = unknown,
  TDb = FirestoreInstance,
> = {
  connectionId: ID;
  taskId: ID;
  // The override currently stored for this task on this connection.
  current: TaskCalendarOverride;
  patch: Partial<TaskCalendarOverride>;
  doc: (db: NoInfer<TDb>, path: string, ...rest: string[]) => TDocRef;
  updateDoc: (
    ref: NoInfer<TDocRef>,
    data: Record<string, unknown>,
  ) => Promise<void>;
  db: TDb;
  serverTimestamp: () => TFieldValue;
  deleteField: () => TFieldValue;
  onWriteError: (error: unknown) => void;
};

// A merge leaving no fields means the entry overrides nothing anymore — it is
// deleted so the task falls back to the linked-item defaults.
const mergeOverride = (
  current: TaskCalendarOverride,
  patch: Partial<TaskCalendarOverride>,
): TaskCalendarOverride | null => {
  const merged = { ...current, ...patch };

  if (merged.synced === undefined && merged.detailsVisibility === undefined) {
    return null;
  }

  return {
    ...(merged.synced !== undefined && { synced: merged.synced }),
    ...(merged.detailsVisibility !== undefined && {
      detailsVisibility: merged.detailsVisibility,
    }),
  };
};

export const setTaskCalendarOverride = <
  TDocRef,
  TFieldValue,
  TDb = FirestoreInstance,
>(
  options: SetTaskCalendarOverrideOptions<TDocRef, TFieldValue, TDb>,
): void => {
  const {
    connectionId,
    taskId,
    current,
    patch,
    doc,
    updateDoc,
    db,
    serverTimestamp,
    deleteField,
    onWriteError,
  } = options;

  const ref = doc(db, FirestoreCollections.ExternalConnections, connectionId);
  const override = mergeOverride(current, patch);

  detachWrite(
    updateDoc(ref, {
      [`settings.taskOverrides.${taskId}`]:
        override === null ? deleteField() : override,
      updatedAt: serverTimestamp(),
    }),
    onWriteError,
  );
};
